import axios from 'redaxios'
import { API_BASE_URL, AUTH_BASE_URL } from '../api/constants'
import { IdentityResponse } from '../api/types/Identity'
import { makeUrl, THIS_BASE_HOST } from '../utils/make.url'
import { parseJWT } from '../utils/parseJWT'

export class OAuth {
  static TOKEN?: string

  static async fetchProfile(): Promise<IdentityResponse | void> {
    try {
      const res = await axios.get(`/oauth/me`, {
        headers: { Authorization: `Bearer ${this.TOKEN}` },
        baseURL: API_BASE_URL,
      })
      return res.data.data
    } catch (error) {
      // If fetching the profile errors because of an unauthorized error, then
      // log the user out so they can re-authenticate.
      if ((error as any)?.statusText === 'Unauthorized') {
        return this.logout()
      }

      throw error
    }
  }

  /**
   * Throw a suspense promise and redirect to the logout flow
   */
  static logout(): void {
    localStorage.removeItem('token')
    window.location.href = makeUrl(AUTH_BASE_URL, '/oauth/logout')
  }

  /**
   * Throw a suspense promise and redirect to the authorization flow
   */
  static authorize(): never {
    throw new Promise(() => {
      window.location.href = makeUrl(AUTH_BASE_URL, `/oauth/authorize`, {
        reset: true,
        response_type: 'code',
        state: 'axxx',
        client_id: 'prepare',
        code_challenge: 'xxxx',
        code_challenge_method: 'PLAIN',
        redirect_uri: makeUrl(THIS_BASE_HOST, window.location.pathname),
      })
    })
  }

  static async exchangeCodeForToken(code: string): Promise<string> {
    const res = await axios.post(
      `/oauth/token`,
      {
        code,
        client_id: 'prepare',
        grant_type: 'authorization_code',
        code_verifier: 'xxxx',
        redirect_uri: THIS_BASE_HOST,
      },
      {
        baseURL: API_BASE_URL,
      }
    )
    const token = res.data.access_token

    if (!token) {
      throw new Error('No access token in response')
    }

    this.setToken(token)

    return token
  }

  public static setToken(token: string) {
    this.TOKEN = token
  }

  private static authTimeoutId: NodeJS.Timeout | undefined

  /**
   * Set a timer to re-authenticate the user 5 minutes before their token expires.
   */
  public static initiateToken(token: string | undefined) {
    if (this.authTimeoutId) {
      clearTimeout(this.authTimeoutId)
    }

    if (!token) {
      return
    }

    this.setToken(token)

    const expirationTime = this.getSecondsUntilExpiration(token)

    // Less than 5 minutes logout immediately
    if (expirationTime <= 5 * 60) {
      return this.logout()
    }

    // Set a timer for 5m before expiration
    this.authTimeoutId = setTimeout(
      () => {
        this.initiateToken(token)
      },
      (expirationTime - 5 * 60) * 1000
    )
  }

  /**
   * Utility function which returns the time (in seconds) remaining until the
   * JWT token expires
   *
   * @returns Time (in seconds) until JWT expires
   */
  private static getSecondsUntilExpiration(token: string): number {
    if (!token) {
      return 0
    }
    const decodedToken = parseJWT(token)
    const currentTimestamp = Math.floor(Date.now() / 1000)
    const expirationTime = decodedToken.exp
    const timeUntilExpiration = expirationTime - currentTimestamp

    if (!decodedToken.exp) {
      return 0
    }
    if (currentTimestamp >= expirationTime) {
      return 0
    }

    return timeUntilExpiration
  }
}
